/*
 * Shared "download this report as a PDF" pipeline.
 *
 * Every report in the deal panel renders as ordinary React + Tailwind in
 * the app document, so there is no server-rendered artifact to hand to a
 * PDF service. Instead we do what the HTML tearsheet already does: clone
 * the rendered report into a throwaway iframe that carries the app's own
 * stylesheets, layer a print stylesheet on top, and let the browser's own
 * renderer produce the PDF via Save-as-PDF.
 *
 * Why print and not html2canvas/html2pdf (which metrics + the GenUI
 * tearsheet path still use):
 *
 *  - html2canvas rasterizes. Text stops being selectable or searchable,
 *    hairlines and small type go soft, and the file balloons. Printing
 *    keeps everything vector, at native size, with the real webfonts.
 *  - html2canvas reimplements CSS layout. Anything it does not model
 *    (container queries, `:has()`, sticky, modern gradients, SVG charts
 *    with foreignObject) silently renders wrong. The print path uses the
 *    same engine that painted the screen, so what you see is what you get.
 *  - Pagination is real. The browser breaks pages using the `break-*`
 *    rules below instead of slicing a tall bitmap mid-row.
 *
 * The one thing the print path cannot do is name the file — Chrome's
 * Save-as-PDF seeds the filename from `document.title`, so that is what
 * `buildFileTitle` is for.
 */

import { forceFullWidthLayout } from "@/components/reports/tearsheet-html";

const MM_PER_INCH = 25.4;
const CSS_PX_PER_INCH = 96;

const mmToPx = (mm: number) => (mm / MM_PER_INCH) * CSS_PX_PER_INCH;

/** Paper the report is laid out on. */
export type PdfOrientation = "portrait" | "landscape";

/**
 * A4 in millimetres. The page box is used at full bleed (`@page` margin is
 * vertical-only) so that the width media queries see during printing stays
 * as close to a desktop viewport as A4 allows — 793px, which keeps every
 * `sm:` and `md:` Tailwind variant on. Landscape reaches 1122px and keeps
 * `lg:` on too, which is what wide tables and chart grids want.
 *
 * Horizontal breathing room comes from padding *inside* the page instead,
 * so it does not shrink the media-query width.
 */
const PAGE = {
  portrait: { widthMm: 210, heightMm: 297 },
  landscape: { widthMm: 297, heightMm: 210 },
} as const;

/** Vertical `@page` margin — repeats on every sheet. */
const PAGE_MARGIN_Y_MM = 12;
/** Horizontal padding applied to the content wrapper, not to `@page`. */
const PAGE_PADDING_X_MM = 10;

/** Width the report content actually occupies on the page, in CSS px. */
export function contentWidthPx(orientation: PdfOrientation): number {
  return Math.round(
    mmToPx(PAGE[orientation].widthMm - PAGE_PADDING_X_MM * 2),
  );
}

export interface ReportPdfOptions {
  /** Report name, e.g. "Deal Brief". Drives the page header and filename. */
  reportTitle: string;
  /** Company the report is about, shown in the header and filename. */
  companyName?: string;
  /** Version label shown in the header, e.g. "v3". */
  versionLabel?: string;
  /** Date shown in the header; defaults to now. */
  generatedOn?: string | Date | null;
  /** Paper orientation. Defaults to portrait. */
  orientation?: PdfOrientation;
}

/* -------------------------------------------------------------------- */
/* Stylesheet collection                                                 */
/* -------------------------------------------------------------------- */

/**
 * Every CSS rule currently applied to the app, as text, plus the hrefs of
 * the sheets we are not allowed to read.
 *
 * Cross-origin sheets (Google Fonts) throw on `.cssRules`; those get
 * re-linked in the print document instead so the real webfonts still load.
 * Without this the PDF silently falls back to Times New Roman.
 */
function collectDocumentStyles(): { css: string; hrefs: string[] } {
  const css: string[] = [];
  const hrefs: string[] = [];

  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList | null = null;
    try {
      rules = sheet.cssRules;
    } catch {
      rules = null;
    }
    if (rules) {
      for (const rule of Array.from(rules)) css.push(rule.cssText);
    } else if (sheet.href) {
      hrefs.push(sheet.href);
    }
  }

  // Constructable stylesheets (none today, but cheap to support and
  // invisible in `document.styleSheets` on older engines).
  for (const sheet of Array.from(document.adoptedStyleSheets ?? [])) {
    try {
      for (const rule of Array.from(sheet.cssRules)) css.push(rule.cssText);
    } catch {
      /* ignore */
    }
  }

  return { css: css.join("\n"), hrefs };
}

/* -------------------------------------------------------------------- */
/* Clone preparation                                                     */
/* -------------------------------------------------------------------- */

/**
 * Replaces every `<canvas>` in the clone with a snapshot `<img>`.
 *
 * A cloned canvas is blank — the bitmap lives on the element, not in the
 * markup — so any canvas-backed chart would print as an empty box. Reports
 * are recharts (SVG) today, but data-lake panels and future charts are not
 * guaranteed to be, and a blank rectangle is a bad failure mode.
 */
function inlineCanvasSnapshots(source: HTMLElement, clone: HTMLElement): void {
  const sourceCanvases = Array.from(source.querySelectorAll("canvas"));
  const cloneCanvases = Array.from(clone.querySelectorAll("canvas"));

  sourceCanvases.forEach((canvas, i) => {
    const target = cloneCanvases[i];
    if (!target) return;
    let dataUrl: string;
    try {
      dataUrl = canvas.toDataURL("image/png");
    } catch {
      // Tainted by a cross-origin draw. Leave the empty canvas rather than
      // failing the whole export.
      return;
    }
    const img = target.ownerDocument.createElement("img");
    img.src = dataUrl;
    const rect = canvas.getBoundingClientRect();
    img.style.width = `${rect.width}px`;
    img.style.height = `${rect.height}px`;
    target.replaceWith(img);
  });
}

/**
 * Strips things that belong to the interactive surface, not the document:
 * anything explicitly opted out with `data-pdf-hide`, plus scripts that
 * would otherwise re-execute inside the print frame.
 */
function stripNonPrintable(clone: HTMLElement): void {
  clone.querySelectorAll("[data-pdf-hide]").forEach((el) => el.remove());
  clone.querySelectorAll("script").forEach((el) => el.remove());
}

/**
 * Prints collapsed text at full length.
 *
 * "Read more" is an invitation paper cannot accept, and the text is already in
 * the document — on screen the clamp hides it, on a page it simply loses it.
 * Two mechanisms are in use across the reports and neither is reachable from
 * the stylesheet alone:
 *
 *  - the `line-clamp-*` utilities (insights, deal fit), which need
 *    `-webkit-line-clamp` unset rather than a `max-height` release; and
 *  - an inline `max-height` the markdown blocks animate against (deal brief,
 *    startup score), which no `[class*="max-h-"]` selector can see.
 *
 * Done to the clone rather than in print CSS because both need the *inline*
 * style cleared, and the fade overlay that masks the cut edge has to go with
 * them — left behind, it draws a gradient over the middle of real prose.
 */
function expandClampedText(clone: HTMLElement): void {
  clone
    .querySelectorAll<HTMLElement>('[style*="max-height"]')
    .forEach((el) => {
      el.style.maxHeight = "none";
      // Scoped to elements that were actually clamped, so the decorative
      // `overflow-hidden` the print CSS deliberately leaves alone stays alone.
      el.style.overflow = "visible";
      el.querySelectorAll<HTMLElement>('[class*="bg-gradient-to-t"]').forEach(
        (fade) => fade.remove(),
      );
    });

  clone.querySelectorAll<HTMLElement>('[class*="line-clamp-"]').forEach((el) => {
    el.style.setProperty("-webkit-line-clamp", "unset", "important");
    el.style.setProperty("display", "block", "important");
    el.style.setProperty("overflow", "visible", "important");
    el.style.setProperty("max-height", "none", "important");
  });
}

/* -------------------------------------------------------------------- */
/* Print document                                                        */
/* -------------------------------------------------------------------- */

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * The print stylesheet layered over the app's own CSS.
 *
 * Split into three jobs, in order: define the sheet, neutralise the parts
 * of the app shell that only make sense on screen, and tell the paginator
 * where it may and may not break.
 */
function printStylesheet(orientation: PdfOrientation): string {
  const { widthMm, heightMm } = PAGE[orientation];
  return `
@page {
  /* Explicit dimensions rather than \`A4 ${orientation}\` so the page box is
     the same regardless of the printer's default paper. Horizontal margin
     stays 0 — see PAGE_PADDING_X_MM — and zero side margins also stop
     Chrome drawing its own URL/date header over the report. */
  size: ${widthMm}mm ${heightMm}mm;
  margin: ${PAGE_MARGIN_Y_MM}mm 0;
}

html, body {
  margin: 0;
  padding: 0;
  background: #fff;
  /* Force the light palette: the app's dark tokens hang off a \`.dark\`
     class we deliberately do not copy onto the print document, and this
     stops the UA dark-mode heuristics from re-introducing it. */
  color-scheme: light;
  /* Release the app shell's viewport clamp. \`index.css\` pins
     \`html, body, #root\` to \`h-dvh overflow-hidden\` so the application
     scrolls in panes rather than as a page, and \`collectDocumentStyles\`
     copies that in verbatim along with everything else. There is no
     viewport in paged media: a body one screen tall that hides its
     overflow prints the first sheet and silently drops the rest of the
     report. \`!important\` because the app's rule and this one have the
     same specificity on \`body\`, and \`min-height\` too — \`h-dvh\` is a
     fixed height, but a future \`min-h-dvh\` would clamp just as hard. */
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  overflow: visible !important;
}

/* Print backgrounds, borders and chart fills even when the user leaves
   "Background graphics" unchecked — a report whose cards, badges and
   chart series all lost their fills is not the report they saw. */
*, *::before, *::after {
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}

.pdf-page {
  box-sizing: border-box;
  width: 100%;
  padding: 0 ${PAGE_PADDING_X_MM}mm;
}

/* ---- Neutralise screen-only behaviour ---- */

/* Entry animations would restart in the fresh document and could be
   captured mid-flight, printing half-faded content. */
*, *::before, *::after {
  animation: none !important;
  transition: none !important;
}
.animate-in, [class*="animate-"], [class*="fade-in"], [class*="slide-in"] {
  opacity: 1 !important;
  transform: none !important;
}

/* Scroll containers must spill their full contents onto the page; a
   clipped one would print only its first screenful.
   Deliberately scoped to the *scrollable* overflow utilities:
   \`overflow-hidden\` in this codebase is decorative (it clips card
   corners to their radius, and recharts sets it inline on its wrapper to
   contain axis labels), and forcing those visible would spill fills past
   their rounded edges. */
[class*="overflow-auto"], [class*="overflow-scroll"],
[class*="overflow-x-auto"], [class*="overflow-y-auto"],
[class*="overflow-x-scroll"], [class*="overflow-y-scroll"],
[data-radix-scroll-area-viewport] {
  overflow: visible !important;
  max-height: none !important;
}
[class*="max-h-"] {
  max-height: none !important;
}

/* Collapsed prose prints in full — see \`expandClampedText\`, which does the
   part a stylesheet cannot reach. This is the backstop for clamps that carry
   a utility class rather than an inline style. */
[class*="line-clamp-"] {
  -webkit-line-clamp: unset !important;
  display: block !important;
  overflow: visible !important;
  max-height: none !important;
}

/* Sticky/fixed chrome anchors to the viewport, which does not exist in
   paged media — it would either float over the content or repeat. */
[class*="sticky"], [class*="fixed"] {
  position: static !important;
}

/* Interactive affordances that carry no information on paper. Elements
   the report marked \`data-pdf-hide\` are already gone — they are deleted
   from the clone, not merely hidden — so this only catches the floating
   layers a report cannot easily annotate. */
button[aria-haspopup], [role="menu"], [role="tooltip"], [role="dialog"],
[data-radix-popper-content-wrapper], .recharts-tooltip-wrapper {
  display: none !important;
}

/* Hover-only reveals must be visible: nothing is hovered on paper. */
.group-hover\\:opacity-100, .hover\\:opacity-100 {
  opacity: 1 !important;
}

/* ---- Pagination ---- */

h1, h2, h3, h4 {
  break-after: avoid-page;
  break-inside: avoid-page;
}
/* Cards, callouts and chart blocks read as units — split one across a
   sheet boundary and the header lands alone at the bottom of a page.
   Chrome still splits any block taller than a page, so this is safe.
   The radii listed are the card/panel ones; \`rounded\`, \`rounded-md\` and
   \`rounded-full\` are badges and pills, and marking those (or every
   \`[class*="border"]\`, which is very nearly every element) would strand
   so much content that pages would end half-empty. */
section, article, figure, li, .recharts-wrapper,
[class*="rounded-lg"], [class*="rounded-xl"], [class*="rounded-2xl"] {
  break-inside: avoid-page;
}
/* Tables are the exception: a long one *should* flow across sheets, with
   its header repeated, breaking only between rows. */
table { break-inside: auto !important; }
thead { display: table-header-group; }
tr, img, svg { break-inside: avoid; }

/* ---- Report header ---- */

.pdf-report-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e0da;
  break-inside: avoid;
  break-after: avoid;
}
.pdf-report-title {
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 26px;
  line-height: 1.15;
  color: #17171a;
}
.pdf-report-subject {
  font-family: "IBM Plex Sans", ui-sans-serif, system-ui, sans-serif;
  font-size: 12px;
  color: #6b6862;
  margin-top: 3px;
}
.pdf-report-meta {
  font-family: "IBM Plex Sans", ui-sans-serif, system-ui, sans-serif;
  font-size: 11px;
  color: #6b6862;
  text-align: right;
  white-space: nowrap;
}
`;
}

/** `YYYY-MM-DD`, tolerating a missing or unparseable date. */
function isoDate(value: string | Date | null | undefined): string {
  const time = value ? new Date(value).getTime() : Date.now();
  if (Number.isNaN(time)) return new Date().toISOString().split("T")[0];
  return new Date(time).toISOString().split("T")[0];
}

function longDate(value: string | Date | null | undefined): string {
  const time = value ? new Date(value).getTime() : Date.now();
  const date = Number.isNaN(time) ? new Date() : new Date(time);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * The document title, which Chrome's Save-as-PDF uses as the default
 * filename. Slashes and colons are stripped because they are path
 * separators on the platforms we support.
 */
export function buildFileTitle(options: ReportPdfOptions): string {
  return [options.companyName, options.reportTitle, options.versionLabel]
    .filter(Boolean)
    .concat(isoDate(options.generatedOn))
    .join(" — ")
    .replace(/[/\\:*?"<>|]/g, "-");
}

function reportHeaderHtml(options: ReportPdfOptions): string {
  const subject = [options.companyName].filter(Boolean).join("");
  return `<header class="pdf-report-header">
  <div>
    <div class="pdf-report-title">${escapeHtml(options.reportTitle)}</div>
    ${subject ? `<div class="pdf-report-subject">${escapeHtml(subject)}</div>` : ""}
  </div>
  <div class="pdf-report-meta">
    ${options.versionLabel ? `${escapeHtml(options.versionLabel)}<br />` : ""}
    ${escapeHtml(longDate(options.generatedOn))}
  </div>
</header>`;
}

function buildPrintDocument(
  body: string,
  options: ReportPdfOptions,
  orientation: PdfOrientation,
): string {
  const { css, hrefs } = collectDocumentStyles();
  const links = hrefs
    .map((href) => `<link rel="stylesheet" href="${escapeHtml(href)}" />`)
    .join("\n");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<!-- Keeps relative url() references in the inlined CSS resolving exactly
     as they do on the live page; a srcdoc frame otherwise has no base. -->
<base href="${escapeHtml(document.baseURI)}" />
<title>${escapeHtml(buildFileTitle(options))}</title>
${links}
<style>${css}</style>
<style>${printStylesheet(orientation)}</style>
</head>
<body>
<div class="pdf-page">
${reportHeaderHtml(options)}
${body}
</div>
</body>
</html>`;
}

/* -------------------------------------------------------------------- */
/* Printing                                                              */
/* -------------------------------------------------------------------- */

/** Two rAFs plus a beat, so ResizeObserver-driven charts re-render. */
function settleLayout(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // recharts' ResponsiveContainer re-renders from a ResizeObserver
        // callback, which lands after the frame that resized it. Without
        // this beat we would serialize the chart at its old width.
        window.setTimeout(resolve, 150);
      });
    });
  });
}

/** Resolves once every image in `doc` has loaded, errored, or timed out. */
function waitForImages(doc: Document, timeoutMs = 8000): Promise<void> {
  const pending = Array.from(doc.images).filter((img) => !img.complete);
  if (pending.length === 0) return Promise.resolve();
  return Promise.race([
    Promise.all(
      pending.map(
        (img) =>
          new Promise<void>((resolve) => {
            img.addEventListener("load", () => resolve(), { once: true });
            img.addEventListener("error", () => resolve(), { once: true });
          }),
      ),
    ).then(() => undefined),
    new Promise<void>((resolve) => window.setTimeout(resolve, timeoutMs)),
  ]);
}

/**
 * Prints an HTML document through a temporary off-screen iframe.
 *
 * Exported because the HTML tearsheet uses the same mechanism: its visible
 * iframe is sandboxed without `allow-modals`, so it cannot call `print()`
 * on itself.
 */
export function printViaTempIframe(html: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    frame.srcdoc = html;

    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      if (frame.parentNode) frame.parentNode.removeChild(frame);
    };

    // Backstop in case `srcdoc` never fires `load` (malformed HTML).
    // Cleared as soon as `load` runs so we don't reject after success.
    const loadTimeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("Print preview timed out"));
    }, 20000);

    frame.addEventListener("load", async () => {
      window.clearTimeout(loadTimeout);
      try {
        const doc = frame.contentDocument;
        const win = frame.contentWindow;
        if (!doc || !win) throw new Error("Print frame has no document");
        // Wait for webfonts and images so the preview doesn't open on a
        // fallback face or a set of empty image boxes.
        if (doc.fonts?.ready) await doc.fonts.ready;
        await waitForImages(doc);
        // Cleanup is driven off `afterprint` rather than a fixed
        // post-`print()` timer: Chromium's `print()` returns immediately
        // while the preview is still reading the iframe, so removing the
        // frame on a short delay races the renderer and blanks the preview.
        win.addEventListener("afterprint", cleanup);
        // Absolute-max safety net so the frame never leaks if `afterprint`
        // is missed (older browsers, sandbox quirks).
        window.setTimeout(cleanup, 120_000);
        win.focus();
        win.print();
        resolve();
      } catch (err) {
        cleanup();
        reject(err);
      }
    });

    document.body.appendChild(frame);
  });
}

/* -------------------------------------------------------------------- */
/* Entry point                                                           */
/* -------------------------------------------------------------------- */

/**
 * The standalone print document for a live report element.
 *
 * Split out from `exportReportToPdf` so the interesting part — what ends
 * up in the document and what gets dropped on the way — can be asserted
 * without a print dialog.
 */
export function buildReportPrintDocument(
  element: HTMLElement,
  options: ReportPdfOptions,
): string {
  const orientation = options.orientation ?? "portrait";
  const clone = element.cloneNode(true) as HTMLElement;
  inlineCanvasSnapshots(element, clone);
  stripNonPrintable(clone);
  expandClampedText(clone);
  // The report root carries panel padding (`p-5 pb-6`), a `max-w-7xl` cap
  // and the capture width forced onto it below. The page supplies its own
  // margins and width, so drop all of them rather than doubling up.
  clone.style.width = "";
  clone.style.minWidth = "";
  clone.style.maxWidth = "none";
  clone.style.padding = "0";
  return buildPrintDocument(clone.outerHTML, options, orientation);
}

/**
 * Renders `element` — a live, on-screen report root — to a PDF via the
 * browser's print dialog.
 *
 * The element is briefly re-laid-out at the printable width *before* it is
 * cloned. That is not cosmetic: recharts sizes its SVG from a measurement
 * of the live container, so a report captured while docked in a 480px side
 * panel would otherwise print a 480px chart stranded in a 718px column.
 * The widening happens in place (not on a clone) precisely so those charts
 * get a real resize to react to.
 */
export async function exportReportToPdf(
  element: HTMLElement,
  options: ReportPdfOptions,
): Promise<void> {
  const orientation = options.orientation ?? "portrait";
  const restore = forceFullWidthLayout(element, contentWidthPx(orientation));
  let html: string;
  try {
    await settleLayout();
    html = buildReportPrintDocument(element, options);
  } finally {
    restore();
  }
  await printViaTempIframe(html);
}
